import React, { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import CheckboxCheckedIcon from '@atlaskit/icon/core/checkbox-checked';
import type { AuthProvider } from '@atlaskit/media-core/auth';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useIsDisabledContext } from '../../../../controllers/disabled-context';
import {
	ExternalSiteImageThumbnail,
	NoImageThumbnail,
	PlaceholderThumbnail,
	UnsplashImageThumbnail,
	UploadedImageThumbnail,
} from '../thumbnail';
import {
	type CustomImage,
	type GenericUnsplashImage,
	type SelectedImage,
	type Thumbnail,
	ThumbnailType,
	type UnsplashImage,
	type UnsplashResult,
} from '../types';

import { createUnsplashDefaultImageThumbnail, getFirstImage } from './utils';

const styles = cssMap({
	imagesWrapper: {
		marginTop: token('space.100'),
		justifyContent: 'space-between',
		paddingTop: token('space.0'),
	},
	checkboxWrapper: {
		position: 'relative',
	},
	selectedImageTick: {
		position: 'absolute',
		bottom: token('space.0'),
		right: token('space.0'),
		'&::before': {
			content: '""',
			backgroundColor: token('color.background.accent.gray.subtlest'),
			height: '50%',
			width: '50%',
			position: 'absolute',
			right: token('space.050'),
			top: token('space.050'),
		},
	},
});

const isUnsplashDefaultImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: UnsplashResult | undefined } => {
	return thumbnail.type === ThumbnailType.UNSPLASH_DEFAULT_IMAGE;
};

const isUnsplashImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: UnsplashImage } => {
	return thumbnail.type === ThumbnailType.UNSPLASH_IMAGE;
};

const isCustomImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: CustomImage } => {
	return thumbnail.type === ThumbnailType.CUSTOM_IMAGE;
};

const isExternalSiteImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: string } => {
	return thumbnail.type === ThumbnailType.EXTERNAL_SITE_IMAGE;
};

const isNoImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: undefined } => {
	return thumbnail.type === ThumbnailType.NO_IMAGE;
};

const createNoImageThumbnail = (): Thumbnail => ({
	type: ThumbnailType.NO_IMAGE,
	image: undefined,
});

export const renderImagePlaceholders = (): React.JSX.Element[] => {
	return Array(5)
		.fill(null)
		.map((_, index) => <PlaceholderThumbnail key={index} />);
};

export type DefaultImagePickerProps = {
	analyticsSource: string;
	collectionName?: string;
	confluencePageId?: string;
	contentId: string;
	enablePaddingBottom?: boolean;
	handleImageSelection: (props: SelectedImage) => void;
	imageAltText?: string;
	initialUnsplashResults: UnsplashResult[] | undefined;
	initialUploadedImageId?: string;
	isNoImageAllowed: boolean;
	isUploadingImage: boolean;
	mediaConfigOverride?: {
		authProvider?: AuthProvider;
		collection?: string;
	};
	previewUploadedImageId?: string;
	selectedImageUrl?: string;
};

/**
 *
 * @param contentId - The sections content id. Used to store all the data for section.
 * @param imageAltText - Text for the image.
 * @param selectedImageUrl - The current selected image url, if one exists. Can be an unsplash image or uploaded image.
 * @param handleImageSelection - Function to handle selecting an image
 * @param initialUnsplashResults - The default unsplash results to populate thumbnails
 * @param analyticsSource - For analytics tracking
 * @param isUploadingImage - Disables the entire component if true
 * @param isNoImageAllowed - Render a thumbnail option for providing no image if true
 * @param initialUploadedImageId - A ref for the initial uploaded image id. To store value between renders. Used to determine first thumbnail
 * @param previewUploadedImageId - Stores image id value when user uploads an image. Used to determine first thumbnail
 * @returns a group of thumbnails for user to select an image.
 */
export const DefaultImagePicker = ({
	contentId,
	imageAltText,
	selectedImageUrl,
	handleImageSelection,
	initialUnsplashResults,
	analyticsSource,
	isUploadingImage,
	isNoImageAllowed,
	initialUploadedImageId,
	previewUploadedImageId,
	mediaConfigOverride,
}: DefaultImagePickerProps): React.JSX.Element => {
	const isDisabled = useIsDisabledContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onImageClick = useCallback(
		({ fullSizeImageUrl, altDescription, downloadLink }: SelectedImage) => {
			if (!isUploadingImage) {
				handleImageSelection({
					fullSizeImageUrl,
					altDescription,
					downloadLink,
				});

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'selected',
						actionSubject: 'image',
						source: analyticsSource,
						attributes: {
							imageSource: 'unsplashDefault',
						},
					},
				}).fire();
			}
		},
		[analyticsSource, createAnalyticsEvent, handleImageSelection, isUploadingImage],
	);

	const renderCheckboxIcon = useCallback(() => {
		return (
			<Box xcss={styles.selectedImageTick}>
				<Flex xcss={styles.checkboxWrapper}>
					<CheckboxCheckedIcon label="checkmark" color={token('color.icon.brand')} />
				</Flex>
			</Box>
		);
	}, []);

	const renderGenericUnsplashImage = useCallback(
		({
			smallSizeImageUrl,
			fullSizeImageUrl,
			description,
			isSelected,
			index,
			downloadLink,
		}: GenericUnsplashImage) => {
			return (
				<UnsplashImageThumbnail
					key={`thumbnail-${index}`}
					image={smallSizeImageUrl}
					description={description}
					isSelected={isSelected}
					canBeSelected={isUploadingImage}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl,
							altDescription: description,
							downloadLink,
						})
					}
				>
					{isSelected && renderCheckboxIcon()}
				</UnsplashImageThumbnail>
			);
		},
		[isUploadingImage, onImageClick, renderCheckboxIcon],
	);

	const renderUnsplashDefaultImage = useCallback(
		(image: UnsplashResult, index: number) => {
			const { fullSizeImageUrl, altDescription, downloadLink, smallSizeImageUrl } = image;
			const isSelected = fullSizeImageUrl === selectedImageUrl;

			return renderGenericUnsplashImage({
				smallSizeImageUrl: smallSizeImageUrl ?? fullSizeImageUrl,
				fullSizeImageUrl,
				description: altDescription,
				isSelected,
				index,
				downloadLink,
			});
		},
		[selectedImageUrl, renderGenericUnsplashImage],
	);

	const renderUnsplashImage = useCallback(
		(image: UnsplashImage, index: number) => {
			const { fullSizeImageUrl, altDescription } = image;
			const isSelected = fullSizeImageUrl === selectedImageUrl;

			return renderGenericUnsplashImage({
				smallSizeImageUrl: fullSizeImageUrl,
				fullSizeImageUrl,
				description: altDescription ?? '',
				isSelected,
				index,
				downloadLink: undefined,
			});
		},
		[selectedImageUrl, renderGenericUnsplashImage],
	);

	const renderExternalSiteImage = useCallback(
		(image: string, index: number) => {
			const isSelectedImage = image === selectedImageUrl;
			return (
				<ExternalSiteImageThumbnail
					key={`thumbnail-${index}`}
					image={image}
					isSelected={isSelectedImage}
					canBeSelected={isUploadingImage}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: image,
							altDescription: '',
						})
					}
				>
					{isSelectedImage && renderCheckboxIcon()}
				</ExternalSiteImageThumbnail>
			);
		},
		[selectedImageUrl, isUploadingImage, renderCheckboxIcon, onImageClick],
	);

	const renderUploadedImage = useCallback(
		(image: CustomImage, index: number) => {
			const { imageId } = image;
			const isSelectedImage = imageId === selectedImageUrl && index === 0;

			return (
				<UploadedImageThumbnail
					key={`thumbnail-${index}`}
					contentId={contentId}
					imageId={imageId}
					isSelected={isSelectedImage}
					mediaConfigOverride={mediaConfigOverride}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: imageId,
							altDescription: '',
						})
					}
				>
					{isSelectedImage && renderCheckboxIcon()}
				</UploadedImageThumbnail>
			);
		},
		[selectedImageUrl, contentId, onImageClick, renderCheckboxIcon, mediaConfigOverride],
	);

	const renderNoImageThumbnail = useCallback(
		(index: number) => {
			const isSelected = selectedImageUrl === '';
			return (
				<NoImageThumbnail
					key={index}
					isSelected={isSelected}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: '',
							altDescription: '',
						})
					}
				>
					{isSelected && renderCheckboxIcon()}
				</NoImageThumbnail>
			);
		},
		[selectedImageUrl, onImageClick, renderCheckboxIcon],
	);

	const firstImage = useRef<Thumbnail>();
	firstImage.current = getFirstImage({
		selectedImageUrl,
		initialUnsplashResults,
		isNoImageAllowed,
		firstImage,
		previewUploadedImageId,
		initialUploadedImageId,
		imageAltText,
	});

	const secondImage = isNoImageAllowed
		? createNoImageThumbnail()
		: createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[1]);

	const imagesToDisplay = [
		firstImage.current,
		secondImage,
		createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[2]),
		createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[3]),
		createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[4]),
	];

	const renderThumbnails = useCallback(
		(thumbnail: Thumbnail, index: number) => {
			// Ordering matters for first image rendering logic
			if (isNoImageThumbnail(thumbnail)) {
				return renderNoImageThumbnail(index);
			}

			if (!thumbnail.image) {
				return <PlaceholderThumbnail key={index} />;
			}

			if (isUnsplashDefaultImageThumbnail(thumbnail)) {
				return renderUnsplashDefaultImage(thumbnail.image, index);
			}

			if (isUnsplashImageThumbnail(thumbnail)) {
				return renderUnsplashImage(thumbnail.image, index);
			}

			if (isExternalSiteImageThumbnail(thumbnail)) {
				return renderExternalSiteImage(thumbnail.image, index);
			}

			if (isCustomImageThumbnail(thumbnail)) {
				return renderUploadedImage(thumbnail.image, index);
			}

			return <PlaceholderThumbnail key={index} />;
		},
		[
			renderNoImageThumbnail,
			renderUnsplashDefaultImage,
			renderUnsplashImage,
			renderUploadedImage,
			renderExternalSiteImage,
		],
	);

	return (
		<Flex xcss={styles.imagesWrapper} testId="default-image-picker">
			{isDisabled ? renderImagePlaceholders() : imagesToDisplay.map(renderThumbnails)}
		</Flex>
	);
};
